import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { LinkContainer } from "react-router-bootstrap";
import { Table, Button, Form, InputGroup } from "react-bootstrap";
import Loader from "../components/Loader";
import Message from "../components/Message";
import { useDispatch, useSelector } from "react-redux";
import { listUsers, deleteUser, restoreUser } from "../actions/userActions";
import Navbar from "../components/Navbar";
import axios from "axios";

const API_URL = process.env.NODE_ENV === "production" ? "" : "http://localhost:3001";

const ROLE_ORDER = { MAIN_ADMIN: 0, LOCAL_ADMIN: 1, USER: 2 };
const ROLE_LABELS = { MAIN_ADMIN: "Main Admins", LOCAL_ADMIN: "Local Admins", USER: "Users" };

const UserListScreen = ({ history }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const showMyUsersFromUrl = location.search.includes("myUsers=true");
  const createdByParam = searchParams.get("createdBy") || null;
  const dispatch = useDispatch();
  const [showDeleted, setShowDeleted] = useState(false);
  const [search, setSearch] = useState("");
  const [showOnlyMyUsers, setShowOnlyMyUsers] = useState(showMyUsersFromUrl);
  const userList = useSelector((state) => state.userList);
  const { loading, error, users } = userList;
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const userDelete = useSelector((state) => state.userDelete);
  const { success: successDelete } = userDelete;

  useEffect(() => {
    setShowOnlyMyUsers(showMyUsersFromUrl);
  }, [showMyUsersFromUrl]);

  useEffect(() => {
    if (userInfo) {
      dispatch(listUsers(showDeleted));
    } else {
      history.push("/login");
    }
  }, [dispatch, history, successDelete, userInfo, showDeleted]);

  const deleteHandler = (id) => {
    if (window.confirm("Are your sure?")) {
      dispatch(deleteUser(id));
    }
  };

  const restoreHandler = (id) => {
    if (window.confirm("Are you sure you want to restore this user?")) {
      dispatch(restoreUser(id));
    }
  };

  // Build a map from the currently fetched users
  const userById = useMemo(() => {
    if (!users || !users.length) return {};
    return users.reduce((acc, u) => ({ ...acc, [u._id]: u }), {});
  }, [users]);

  // Fetch all users (including deleted) once, to resolve creator names for deleted/missing creators
  const [allUsersMap, setAllUsersMap] = useState({});
  const hasFetchedAllRef = useRef(false);
  useEffect(() => {
    if (!userInfo || hasFetchedAllRef.current) return;
    hasFetchedAllRef.current = true;
    axios
      .get(API_URL + "/api/users?showDeleted=true", {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      })
      .then(({ data }) => {
        setAllUsersMap(data.reduce((acc, u) => ({ ...acc, [u._id]: u }), {}));
      })
      .catch(() => {});
  }, [userInfo]);

  // Merge: prefer current list, fall back to allUsersMap for creator resolution
  const resolvedUserById = useMemo(
    () => ({ ...allUsersMap, ...userById }),
    [allUsersMap, userById]
  );

  const filteredAndGrouped = useMemo(() => {
    if (!users || !users.length) return {};
    const q = search.trim().toLowerCase();
    let filtered = q
      ? users.filter(
          (u) =>
            (u.username && u.username.toLowerCase().includes(q)) ||
            (u.email && u.email.toLowerCase().includes(q)) ||
            (u._id && u._id.toLowerCase().includes(q)) ||
            (u.createdBy && u.createdBy.toLowerCase().includes(q))
        )
      : users;
    if (showOnlyMyUsers && userInfo && userInfo._id) {
      filtered = filtered.filter((u) => u.createdBy === userInfo._id);
    }
    if (createdByParam) {
      filtered = filtered.filter((u) => u.createdBy === createdByParam);
    }
    const byRole = {};
    filtered.forEach((u) => {
      const role = u.role || "USER";
      if (!byRole[role]) byRole[role] = {};
      const creatorId = u.createdBy || "_none_";
      if (!byRole[role][creatorId]) byRole[role][creatorId] = [];
      byRole[role][creatorId].push(u);
    });
    ["MAIN_ADMIN", "LOCAL_ADMIN", "USER"].forEach((role) => {
      if (byRole[role]) {
        Object.keys(byRole[role]).forEach((creatorId) => {
          byRole[role][creatorId].sort((a, b) => (ROLE_ORDER[a.role] ?? 3) - (ROLE_ORDER[b.role] ?? 3));
        });
      }
    });
    return byRole;
  }, [users, search, showOnlyMyUsers, userInfo, createdByParam]);

  const totalFiltered = useMemo(() => {
    return Object.values(filteredAndGrouped).reduce((sum, byCreator) => {
      const count = typeof byCreator === "object" && !Array.isArray(byCreator)
        ? Object.values(byCreator).reduce((s, arr) => s + arr.length, 0)
        : byCreator.length;
      return sum + count;
    }, 0);
  }, [filteredAndGrouped]);

  const renderUserRow = (user) => (
    <tr key={user._id}>
      <td style={{ whiteSpace: "nowrap" }}>{user.username}</td>
      <td style={{ whiteSpace: "nowrap" }}>
        <a href={`mailto:${user.email}`}>{user.email}</a>
      </td>
      <td style={{ whiteSpace: "nowrap" }} className="tw-font-mono tw-text-xs">
        {user.createdBy ? (
          resolvedUserById[user.createdBy] ? (
            <span title={user.createdBy}>{resolvedUserById[user.createdBy].username}</span>
          ) : (
            <span title={user.createdBy} className="tw-text-gray-500 tw-italic">Unknown</span>
          )
        ) : (
          <span className="tw-text-muted">—</span>
        )}
      </td>
      <td style={{ wordBreak: "break-all" }} className="tw-max-w-[200px]">
        {user.role === "MAIN_ADMIN" ? (
          <>Main Admin</>
        ) : user.assignedGensets && user.assignedGensets.length > 0 ? (
          <span title={user.assignedGensets.map((e) => e.key).join(", ")}>
            {user.assignedGensets.length} genset{user.assignedGensets.length !== 1 ? "s" : ""}:{" "}
            {user.assignedGensets.map((e) => e.key).slice(0, 5).join(", ")}
            {user.assignedGensets.length > 5 ? "…" : ""}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td style={{ whiteSpace: "nowrap" }}>{user.role}</td>
      <td style={{ whiteSpace: "nowrap" }}>
        {user.isDeleted ? (
          <span className="tw-text-red-600 tw-font-semibold">Deleted</span>
        ) : (
          <span className="tw-text-green-600 tw-font-semibold">Active</span>
        )}
      </td>
      <td style={{ whiteSpace: "nowrap" }}>
        {(userInfo.role === "MAIN_ADMIN" || userInfo.role === "LOCAL_ADMIN") && (
          <LinkContainer
            to={`/dashboard?userId=${user._id}`}
            className="tw-mx-2 tw-cursor-pointer"
            title="View user's gensets"
          >
            <i className="fas fa-th-large"></i>
          </LinkContainer>
        )}
        {((user.role === "USER" && userInfo.role === "LOCAL_ADMIN") ||
          (user.role === "LOCAL_ADMIN" && userInfo.role === "MAIN_ADMIN") ||
          userInfo.role === "MAIN_ADMIN") && (
          <LinkContainer
            to={`/admin/user/${user._id}/edit`}
            className="tw-mx-2 tw-cursor-pointer"
          >
            <i className="fas fa-edit"></i>
          </LinkContainer>
        )}
        {userInfo && userInfo.role === "MAIN_ADMIN" && !user.isDeleted && (
          <Button
            variant="danger"
            className="btn-sm tw-ml-4"
            onClick={() => deleteHandler(user._id)}
          >
            <i className="fas fa-trash"></i>
          </Button>
        )}
        {userInfo && userInfo.role === "MAIN_ADMIN" && user.isDeleted && (
          <Button
            variant="success"
            className="btn-sm tw-ml-4"
            onClick={() => restoreHandler(user._id)}
            title="Restore User"
          >
            <i className="fas fa-undo"></i>
          </Button>
        )}
      </td>
    </tr>
  );

  return (
    <>
      <Navbar />
      <br />
      <br />
      <br />
      <br />
      <div className="tw-mx-6">
        <h3 className="tw-font-nunito sm:tw-text-3xl tw-text-2xl tw-font-bold tw-text-center tw-my-4">
          User Details
        </h3>
        <h5 className="tw-font-nunito tw-text-xl tw-font-semibold">
          Total Registered Users: {users && users.length}
          {(search.trim() || showOnlyMyUsers || createdByParam) && (
            <span className="tw-text-gray-600 tw-font-normal tw-ml-2">
              (showing {totalFiltered})
            </span>
          )}
        </h5>

        <div className="tw-mb-4 tw-flex tw-flex-wrap tw-gap-3 tw-items-center">
          <InputGroup className="tw-max-w-md">
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search by name, email, or User ID (createdBy)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="tw-font-nunito"
            />
          </InputGroup>
          {(userInfo?.role === "MAIN_ADMIN" || userInfo?.role === "LOCAL_ADMIN") && (
            <Form.Check
              type="checkbox"
              id="showOnlyMyUsers"
              label="Show only users I created"
              checked={showOnlyMyUsers}
              onChange={(e) => setShowOnlyMyUsers(e.target.checked)}
              className="tw-font-nunito"
            />
          )}
          {userInfo && userInfo.role === "MAIN_ADMIN" && (
            <Form.Check
              type="checkbox"
              id="showDeleted"
              label="Show Deleted Users"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="tw-font-nunito"
            />
          )}
        </div>

        <br />
        {loading ? (
          <Loader />
        ) : error ? (
          <Message variant="danger">{error}</Message>
        ) : (
          <Table
            striped
            bordered
            hover
            responsive
            className="table-sm tw-font-nunito myTable"
          >
            <thead
              className="tw-text-center tw-text-gray-100"
              style={{ backgroundColor: "#4a372e" }}
            >
              <tr>
                <th>NAME</th>
                <th>EMAIL</th>
                <th>Created By</th>
                <th>Gensets</th>
                <th>Role</th>
                <th>Status</th>
                <th>UPDATE</th>
              </tr>
            </thead>
            <tbody className="tw-text-center">
              {["MAIN_ADMIN", "LOCAL_ADMIN", "USER"].map((role) => {
                const byCreator = filteredAndGrouped[role];
                if (!byCreator || typeof byCreator !== "object") return null;
                const creatorIds = Object.keys(byCreator);
                if (creatorIds.length === 0) return null;
                const roleTotal = creatorIds.reduce((s, cid) => s + byCreator[cid].length, 0);
                return (
                  <React.Fragment key={role}>
                    <tr style={{ backgroundColor: "rgba(74, 55, 46, 0.2)" }}>
                      <td colSpan={7} className="tw-text-left tw-font-semibold tw-py-2 tw-px-3">
                        {ROLE_LABELS[role] || role} ({roleTotal})
                      </td>
                    </tr>
                    {creatorIds.map((creatorId) => {
                      const roleUsers = byCreator[creatorId];
                      const creatorLabel =
                        creatorId === "_none_"
                          ? "No creator"
                          : resolvedUserById[creatorId]
                            ? resolvedUserById[creatorId].username
                            : "Unknown User";
                      return (
                        <React.Fragment key={`${role}-${creatorId}`}>
                          <tr style={{ backgroundColor: "rgba(74, 55, 46, 0.1)" }}>
                            <td colSpan={7} className="tw-text-left tw-py-1 tw-px-4 tw-text-sm">
                              Created by <strong>{creatorLabel}</strong> ({roleUsers.length})
                            </td>
                          </tr>
                          {roleUsers.map((u) => renderUserRow(u))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>
    </>
  );
};

export default UserListScreen;
